import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { FacetGroup } from './Shared/facetTypes';
import { fetchAccountFacets, fetchAccountSubs, fetchAccounts, normalizeAppFacet } from '@/api/accounts';
import {
    EMPTY_REALM_FILTERS,
    type AccountSortDirection,
    type AccountSortField,
    type RealmAccountsFilters,
} from './types';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function orderingParam(field: AccountSortField, direction: AccountSortDirection): string {
    return direction === 'desc' ? `-${field}` : field;
}

/**
 * EAA-1820: the realm-wide accounts screen's data — everything
 * `useAccountsScan.ts` + `useAccountsView.ts` used to compute over a
 * capped-2,000-row client walk, now driven by the two REAL endpoints
 * `GET /api/v1/accounts` (list, real server pagination/search/filter/sort) and
 * `GET /api/v1/accounts/facets` (rail counts + the four saved-filter counts).
 *
 * `useAccountsScan.ts` is DELETED, not kept as a fallback: it under-counted
 * silently past its 2,000-row cap, and the facets endpoint answers the same
 * question exactly, in one request, for the whole realm (also honestly capped —
 * see `capped` below — but computed server-side rather than by an unbounded
 * client walk).
 */
export interface RealmAccountsAdapterOptions {
    /**
     * EAA-2068: the app-detail screen's scope — every list, subs and facets
     * request carries `app=<code>`, the rail's "Ứng dụng" group is not offered,
     * and clearing filters never drops it. Omitted on `/console/accounts`.
     */
    lockedApp?: string | null;
}

export function useRealmAccountsAdapter(
    apiBase: string,
    accessToken: string | null,
    enabled: boolean,
    options: RealmAccountsAdapterOptions = {}
) {
    const { t } = useTranslation();
    const lockedApp = options.lockedApp || null;
    const queryClient = useQueryClient();

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<RealmAccountsFilters>(EMPTY_REALM_FILTERS);
    const [sortField, setSortField] = useState<AccountSortField>('created');
    const [sortDirection, setSortDirection] = useState<AccountSortDirection>('desc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    // EAA-1829: "Chọn tất cả N dòng khớp bộ lọc". The realm list is server-paged, so a
    // selection can span rows that are not on the current page; their usernames come
    // back with the subs and are kept here for the bulk dialogs and result panels.
    const [selectAllBusy, setSelectAllBusy] = useState(false);
    const [selectAllCapped, setSelectAllCapped] = useState(false);
    const [usernameBySub, setUsernameBySub] = useState<Map<string, string>>(new Map());


    const listQuery = useQuery({
        queryKey: [
            'console-accounts-list',
            accessToken,
            lockedApp,
            search,
            filters,
            sortField,
            sortDirection,
            page,
            pageSize,
        ],
        queryFn: () =>
            fetchAccounts(apiBase, accessToken as string, {
                page,
                page_size: pageSize,
                search,
                status: filters.status ?? undefined,
                permission: filters.permission ?? undefined,
                organization_id: filters.organizationId ?? undefined,
                source: filters.source ?? undefined,
                app: lockedApp ?? filters.app ?? undefined,
                ordering: orderingParam(sortField, sortDirection),
            }),
        enabled: enabled && Boolean(accessToken),
        retry: false,
        staleTime: 15_000,
    });

    // EAA-1820: the rail's counts come from a SEPARATE aggregate endpoint that
    // only takes `search` — narrowing by the rail's own facets is therefore not
    // possible server-side (there is no per-facet-combination count endpoint,
    // same limitation the org screen already has — see `useScopedAccountFacets`).
    // Each group's counts describe "how many match the current SEARCH", not "how
    // many would this option add given the other ticked facets"; that is the one
    // honest simplification this screen makes relative to the old client walk,
    // which could recompute in memory.
    const facetsQuery = useQuery({
        queryKey: ['console-accounts-facets', accessToken, lockedApp, search],
        queryFn: () =>
            fetchAccountFacets(apiBase, accessToken as string, search, lockedApp ? { app: lockedApp } : {}),
        enabled: enabled && Boolean(accessToken),
        retry: false,
        staleTime: 15_000,
    });

    const rows = listQuery.data?.results ?? [];
    const total = listQuery.data?.count ?? 0;
    const totalPages = Math.max(1, listQuery.data?.total_pages ?? 1);
    const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeTo = Math.min(page * pageSize, total);
    const lastLoginAvailable = listQuery.data?.last_login_available ?? false;
    const facets = facetsQuery.data ?? null;
    const capped = Boolean(listQuery.data?.capped) || Boolean(facets?.capped);

    const selectAllMatching = useCallback(async () => {
        if (!accessToken) return;
        setSelectAllBusy(true);
        try {
            const res = await fetchAccountSubs(apiBase, accessToken, {
                search,
                status: filters.status ?? undefined,
                permission: filters.permission ?? undefined,
                organization_id: filters.organizationId ?? undefined,
                source: filters.source ?? undefined,
                app: lockedApp ?? filters.app ?? undefined,
            });
            setUsernameBySub(new Map(res.results.map((r) => [r.sub, r.username])));
            setSelected(new Set(res.results.map((r) => r.sub)));
            setSelectAllCapped(res.capped);
        } finally {
            setSelectAllBusy(false);
        }
    }, [apiBase, accessToken, search, filters, lockedApp]);

    const invalidate = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['console-accounts-list'] });
        void queryClient.invalidateQueries({ queryKey: ['console-accounts-facets'] });
    }, [queryClient]);

    const patchFilters = useCallback((patch: Partial<RealmAccountsFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
        setPage(1);
    }, []);

    const hasActiveFilters =
        Boolean(search.trim()) ||
        filters.status !== null ||
        filters.permission !== null ||
        filters.source !== null ||
        filters.organizationId !== null ||
        (!lockedApp && filters.app !== null);

    const activeFilterCount =
        (search.trim() ? 1 : 0) +
        (filters.status !== null ? 1 : 0) +
        (filters.permission !== null ? 1 : 0) +
        (filters.source !== null ? 1 : 0) +
        (filters.organizationId !== null ? 1 : 0) +
        (!lockedApp && filters.app !== null ? 1 : 0);

    const clearFilters = useCallback(() => {
        setSearch('');
        setFilters(EMPTY_REALM_FILTERS);
        setPage(1);
    }, []);

    const selectFacet = useCallback(
        (groupKey: string, optionKey: string) => {
            if (groupKey === 'status') {
                patchFilters({ status: filters.status === optionKey ? null : (optionKey as never) });
            } else if (groupKey === 'permission') {
                patchFilters({
                    permission: filters.permission === optionKey ? null : (optionKey as never),
                });
            } else if (groupKey === 'source') {
                patchFilters({ source: filters.source === optionKey ? null : (optionKey as never) });
            } else if (groupKey === 'organization') {
                patchFilters({
                    organizationId: filters.organizationId === optionKey ? null : optionKey,
                });
            } else if (groupKey === 'app' && !lockedApp) {
                patchFilters({ app: filters.app === optionKey ? null : optionKey });
            }
        },
        [filters, patchFilters, lockedApp]
    );

    const facetGroups: FacetGroup[] = useMemo(() => {
        const c = facets ?? {
            status: { active: 0, pending: 0, locked: 0 },
            permission: { granted: 0, none: 0 },
            source: { console: 0, unknown: 0 },
            organizations: [] as { organization_id: string; organization_name: string; count: number }[],
        };
        const groups: FacetGroup[] = [
            {
                key: 'status',
                label: t('console.accounts.facetRail.statusGroupTitle'),
                options: [
                    {
                        key: 'active',
                        label: t('console.accounts.facetRail.statusEnabled'),
                        count: facets ? c.status.active : null,
                        selected: filters.status === 'active',
                    },
                    {
                        key: 'pending',
                        label: t('console.accounts.facetRail.statusPending'),
                        count: facets ? c.status.pending : null,
                        selected: filters.status === 'pending',
                    },
                    {
                        key: 'locked',
                        label: t('console.accounts.facetRail.statusDisabled'),
                        count: facets ? c.status.locked : null,
                        selected: filters.status === 'locked',
                    },
                ],
            },
            {
                key: 'permission',
                label: t('console.accounts.facetRail.permissionGroupTitle'),
                options: [
                    {
                        key: 'granted',
                        label: t('console.accounts.facetRail.permissionGranted'),
                        count: facets ? c.permission.granted : null,
                        selected: filters.permission === 'granted',
                    },
                    {
                        key: 'none',
                        label: t('console.accounts.facetRail.permissionNone'),
                        count: facets ? c.permission.none : null,
                        selected: filters.permission === 'none',
                    },
                ],
            },
            {
                key: 'organization',
                label: t('console.accounts.facetRail.organizationGroupTitle'),
                options: c.organizations.map((org) => ({
                    key: org.organization_id,
                    label: org.organization_name,
                    count: org.count,
                    selected: filters.organizationId === org.organization_id,
                })),
            },
            {
                key: 'source',
                label: t('console.accounts.facetRail.sourceGroupTitle'),
                options: [
                    {
                        key: 'console',
                        label: t('console.accounts.facetRail.sourceConsole'),
                        count: facets ? c.source.console : null,
                        selected: filters.source === 'console',
                    },
                    {
                        key: 'unknown',
                        label: t('console.accounts.facetRail.sourceUnknown'),
                        count: facets ? c.source.unknown : null,
                        selected: filters.source === 'unknown',
                    },
                ],
            },
        ];
        // EAA-2068: "Ứng dụng", after "Đơn vị" (both list real entities rather
        // than fixed values). Not offered under a locked scope — every row there
        // already belongs to that one app, so the group would be a single,
        // un-clearable option.
        if (!lockedApp) {
            const appGroup: FacetGroup = {
                key: 'app',
                label: t('console.accounts.facetRail.appGroupTitle'),
                options: normalizeAppFacet(facets?.apps).map((app) => ({
                    key: app.app_code,
                    label: app.app_name,
                    count: app.count,
                    selected: filters.app === app.app_code,
                })),
            };
            const orgIndex = groups.findIndex((g) => g.key === 'organization');
            groups.splice(orgIndex + 1, 0, appGroup);
        }
        return groups;
    }, [facets, filters, lockedApp, t]);


    function onSort(field: AccountSortField) {
        if (field === sortField) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection(field === 'created' ? 'desc' : 'asc');
        }
        setPage(1);
    }

    return {
        lockedApp,
        search,
        setSearch: (value: string) => {
            setSearch(value);
            setPage(1);
        },
        filters,
        hasActiveFilters,
        activeFilterCount,
        clearFilters,
        selectFacet,
        facetGroups,
        rows,
        total,
        capped,
        lastLoginAvailable,
        listQuery,
        facetsQuery,
        invalidate,

        sortField,
        sortDirection,
        onSort,

        page,
        setPage,
        pageSize,
        setPageSize: (size: number) => {
            setPageSize(size);
            setPage(1);
        },
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        totalPages,
        rangeFrom,
        rangeTo,

        selected,
        setSelected,
        selectAllMatching,
        selectAllBusy,
        selectAllCapped,
        /** Username for any selected sub — page rows first, then the select-all result. */
        usernameFor: (sub: string) => rows.find((r) => r.sub === sub)?.username ?? usernameBySub.get(sub) ?? sub,
    };
}

export type RealmAccountsAdapter = ReturnType<typeof useRealmAccountsAdapter>;
